import { useState, type ReactNode } from "react";
import { Pencil, Reply, Trash2 } from "lucide-react";
import type { LinkedDocument } from "@/domains/pm/types";
import { UserAvatar, type UserAvatarUser } from "@/components/ui/user-avatar";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { MentionTextarea, type MentionUser } from "@/domains/pm/components/issues/MentionTextarea";
import { DocumentBreadcrumbHover } from "@/domains/pm/components/issues/DocumentBreadcrumbHover";
import { LinkedDocumentRow } from "@/domains/pm/components/issues/LinkedDocumentRow";
import { splitQuoteRuns } from "@/domains/pm/utils/commentQuote";
import { splitMentionRuns } from "@/domains/pm/utils/mentionHighlight";
import { useOpenLinkedDoc } from "@/domains/pm/hooks/useOpenLinkedDoc";
import { DocChip, DocChipCollapsed } from "@/components/chat/SidebarChat/DocLink/DocChip";
import type { ViewerDocumentData } from "@/domains/documents/hooks/useDocumentViewer";
import { cn } from "@/lib/utils";

/** Avatar diameter of a thread row — also the indent of a headerless row. */
export const CHAT_ROW_AVATAR_SIZE = 26;

/** Strings the row renders. Supplied by the caller so the shared row stays
 *  free of any one surface's i18n namespace. */
export interface ChatRowLabels {
  reply: string;
  edit: string;
  edited: string;
  save: string;
  cancel: string;
  /** Delete affordance — only needed when `onDelete` is passed. */
  delete?: string;
  confirmDelete?: string;
  confirmDeleteDescription?: string;
}

/** One paragraph of a message, with `@Name` mentions highlighted. */
function ChatLine({ text, displayNames }: { text: string; displayNames: string[] }) {
  return (
    <div className="whitespace-pre-wrap">
      {splitMentionRuns(text, displayNames).map((run, i) =>
        run.mention ? (
          <span key={`m-${i}`} className="font-bold text-red-600">
            {run.text}
          </span>
        ) : (
          run.text
        )
      )}
    </div>
  );
}

function ChatBody({ body, displayNames }: { body: string; displayNames: string[] }) {
  return (
    <div className="py-0.5 text-sm leading-relaxed">
      {splitQuoteRuns(body).map((run, i) =>
        run.quoted ? (
          <blockquote
            key={`q-${i}`}
            className="my-1 whitespace-pre-wrap border-l-2 border-muted-foreground/30 pl-2 text-xs text-muted-foreground"
          >
            {run.text}
          </blockquote>
        ) : (
          <ChatLine key={`p-${i}`} text={run.text} displayNames={displayNames} />
        )
      )}
    </div>
  );
}

/** Documents attached to a message — one chip, or a collapsed group. */
function ChatAttachments({
  linked,
  buildingId,
  openViewer,
  layout,
}: {
  linked: LinkedDocument[];
  buildingId: number;
  openViewer: (doc: ViewerDocumentData) => Promise<void>;
  layout: "chips" | "rows";
}) {
  // Above the early return on purpose: `linked` goes 0 → 1 when someone
  // attaches a document to an existing message, and a hook behind that return
  // would make React throw "Rendered more hooks than during the previous
  // render" on exactly that update.
  const { canOpen, nonOpenableFilenames, open: openDoc } = useOpenLinkedDoc(buildingId, openViewer);
  if (linked.length === 0) return null;
  if (layout === "rows") {
    return (
      <ul className="mt-1.5 space-y-0.5">
        {linked.map((doc) => (
          <LinkedDocumentRow
            key={doc.id}
            filename={doc.filename}
            buildingId={buildingId}
            breadcrumbSegments={doc.breadcrumbSegments}
            onOpen={canOpen(doc) ? () => openDoc(doc) : undefined}
          />
        ))}
      </ul>
    );
  }
  return (
    <div className="mt-1.5">
      {linked.length === 1 ? (
        // WHA-3457: hover the chip for the interactive folder path.
        <DocumentBreadcrumbHover segments={linked[0].breadcrumbSegments} buildingId={buildingId}>
          <span className="inline-flex">
            <DocChip
              filename={linked[0].filename}
              onClick={canOpen(linked[0]) ? () => openDoc(linked[0]) : undefined}
              showTooltip={false}
            />
          </span>
        </DocumentBreadcrumbHover>
      ) : (
        <DocChipCollapsed
          filenames={linked.map((d) => d.filename)}
          nonOpenableFilenames={nonOpenableFilenames(linked)}
          onDocClick={(filename) => {
            const doc = linked.find((d) => d.filename === filename);
            if (doc) openDoc(doc);
          }}
        />
      )}
    </div>
  );
}

function ActionButton(props: {
  label?: string;
  onClick: () => void;
  className: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={props.onClick}
      aria-label={props.label}
      title={props.label}
      className={props.className}
    >
      {props.children}
    </button>
  );
}

interface ChatRowActionsProps {
  labels: ChatRowLabels;
  canEdit: boolean;
  /** Omitted where the viewer cannot reply — the Reply action is not rendered. */
  onReply?: () => void;
  onStartEdit: () => void;
  onDelete?: () => void;
  /** Floating pill anchored to the row (a headerless, grouped follow-up) vs
   *  the inline run at the end of the author line. */
  floating?: boolean;
}

/** Class sets for the two placements: a floating pill over a headerless row,
 *  or an inline run at the end of the author line. */
function actionStyles(floating: boolean | undefined) {
  return {
    wrapper: cn(
      "flex items-center gap-0.5 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100",
      floating
        ? "absolute -top-2 right-3 z-10 rounded-md border bg-white p-0.5 shadow-sm"
        : "ml-auto self-center"
    ),
    btn: floating
      ? "rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
      : "rounded p-0.5 text-muted-foreground hover:text-foreground",
    icon: floating ? "h-3.5 w-3.5" : "h-3 w-3",
  };
}

/** Hover-revealed reply / edit / delete actions, plus the delete confirmation. */
function ChatRowActions({
  labels,
  canEdit,
  onReply,
  onStartEdit,
  onDelete,
  floating,
}: ChatRowActionsProps) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const { wrapper, btn, icon } = actionStyles(floating);
  return (
    <div className={wrapper}>
      {onReply && (
        <ActionButton label={labels.reply} onClick={onReply} className={btn}>
          <Reply className={icon} />
        </ActionButton>
      )}
      {canEdit && (
        <ActionButton label={labels.edit} onClick={onStartEdit} className={btn}>
          <Pencil className={icon} />
        </ActionButton>
      )}
      {onDelete && (
        <>
          <ActionButton
            label={labels.delete}
            onClick={() => setConfirmOpen(true)}
            className={cn(btn, "hover:text-destructive")}
          >
            <Trash2 className={icon} />
          </ActionButton>
          <ConfirmDialog
            open={confirmOpen}
            onOpenChange={setConfirmOpen}
            onConfirm={onDelete}
            title={labels.confirmDelete}
            description={labels.confirmDeleteDescription}
            confirmText={labels.delete}
          />
        </>
      )}
    </div>
  );
}

/** Author, timestamp and the hover-revealed actions. */
function ChatRowHeader(props: {
  authorName: string;
  timestamp: string;
  edited: boolean;
  labels: ChatRowLabels;
  actions: ReactNode;
}) {
  const { authorName, timestamp, edited, labels, actions } = props;
  return (
    <div className="mb-1 flex items-baseline gap-2">
      <span className="text-xs font-semibold">{authorName}</span>
      <span className="text-[10px] text-muted-foreground">{timestamp}</span>
      {edited && <span className="text-[10px] text-muted-foreground">· {labels.edited}</span>}
      {actions}
    </div>
  );
}

/** Inline editor for one of the user's own messages. */
function ChatRowEditor(props: {
  value: string;
  onChange: (value: string) => void;
  onSave: () => void;
  onCancel: () => void;
  projectId: number;
  users?: MentionUser[];
  labels: ChatRowLabels;
}) {
  const { value, onChange, onSave, onCancel, projectId, users, labels } = props;
  return (
    <div className="mt-1">
      <MentionTextarea
        value={value}
        onChange={onChange}
        onMentionAdd={() => {
          /* edit mode derives mentions from the final text on save */
        }}
        onSubmit={onSave}
        rows={2}
        projectId={projectId}
        users={users}
      />
      <div className="mt-1.5 flex items-center gap-2">
        <Button size="sm" onClick={onSave} disabled={!value.trim()} className="h-7 text-xs">
          {labels.save}
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel} className="h-7 text-xs">
          {labels.cancel}
        </Button>
      </div>
    </div>
  );
}

/** Everything under the author line: the body (or the editor), attachments and
 *  the trailing slot. */
function ChatRowContent(props: ChatThreadRowProps & { showHeader: boolean }) {
  if (props.isEditing) {
    return (
      <ChatRowEditor
        value={props.editBody}
        onChange={props.onEditBodyChange}
        onSave={props.onSaveEdit}
        onCancel={props.onCancelEdit}
        projectId={props.projectId}
        users={props.mentionUsers}
        labels={props.labels}
      />
    );
  }
  return (
    <>
      <ChatBody body={props.body} displayNames={props.mentionDisplayNames} />
      {/* Header rows carry "· edited" inline; grouped follow-ups keep the small
          marker under the body instead. */}
      {!props.showHeader && props.edited && (
        <div className="text-[10px] text-muted-foreground">{props.labels.edited}</div>
      )}
      <ChatAttachments
        linked={props.attachments ?? []}
        buildingId={props.buildingId}
        openViewer={props.openViewer}
        layout={props.attachmentLayout ?? "chips"}
      />
      {props.footer && <div className="mt-1">{props.footer}</div>}
    </>
  );
}

export interface ChatThreadRowProps {
  author: UserAvatarUser | null | undefined;
  /** Replaces the author's avatar — a message written by Franklin rather than a person. */
  avatar?: ReactNode;
  authorName: string;
  /** Already-formatted timestamp — each surface picks its own granularity. */
  timestamp: string;
  edited: boolean;
  body: string;
  /** Project-member display names — the basis for mention highlighting. */
  mentionDisplayNames: string[];
  attachments?: LinkedDocument[];
  /**
   * "rows" lists attachments as the issue panel does — a row per file
   * (brief appendices). Chat surfaces keep the compact chips.
   */
  attachmentLayout?: "chips" | "rows";
  buildingId: number;
  /** Project of the thread — the mention picker in the editor is scoped to it. */
  projectId: number;
  /** An explicit mention audience instead of the project's members. */
  mentionUsers?: MentionUser[];
  openViewer: (doc: ViewerDocumentData) => Promise<void>;
  labels: ChatRowLabels;
  /** False hides the avatar + author line (a grouped chat follow-up). */
  showHeader?: boolean;
  /** Structured quote of a parent message, rendered above the body. */
  quote?: ReactNode;
  /** Trailing slot — the message board's reaction bar. */
  footer?: ReactNode;
  /** Pulses the row after a jump-to-message. */
  highlighted?: boolean;
  /** Rendered as `data-message-id`, so a caller can scroll the row into view. */
  anchorId?: number;
  /** Only the author's own messages get Edit / Delete affordances. */
  canEdit: boolean;
  isEditing: boolean;
  editBody: string;
  onEditBodyChange: (value: string) => void;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onSaveEdit: () => void;
  /** Omitted where the viewer cannot reply (a read-only brief viewer). */
  onReply?: () => void;
  /** Omitted where messages can't be deleted (the issue comment thread). */
  onDelete?: () => void;
  /** No reply / edit / delete affordances at all — a thread shown from the
   *  trash, or to a viewer with no session. */
  readOnly?: boolean;
}

/**
 * One row of a chat thread: author line with hover reply/edit/delete actions,
 * then the body (or the inline editor) and any attached document chips.
 *
 * Shared by the PM issue-panel comment thread and the building Dashboard's
 * message board — one implementation, so both surfaces stay identical.
 */
export function ChatThreadRow(props: ChatThreadRowProps) {
  const { showHeader = true, highlighted, isEditing, readOnly = false } = props;

  const actions =
    isEditing || readOnly ? null : (
      <ChatRowActions
        labels={props.labels}
        canEdit={props.canEdit}
        onReply={props.onReply}
        onStartEdit={props.onStartEdit}
        onDelete={props.onDelete}
        floating={!showHeader}
      />
    );

  return (
    <div
      data-message-id={props.anchorId}
      className={cn("group relative flex gap-2", highlighted && "rounded-md bg-primary/5")}
    >
      {showHeader ? (
        (props.avatar ?? (
          <UserAvatar
            user={props.author}
            size={CHAT_ROW_AVATAR_SIZE}
            className="mt-0.5 flex-shrink-0"
          />
        ))
      ) : (
        // Keep a grouped follow-up aligned under the author's first message.
        <div style={{ width: CHAT_ROW_AVATAR_SIZE }} className="flex-shrink-0" />
      )}
      {!showHeader && actions}

      <div className={cn("min-w-0 flex-1", highlighted && "origin-left animate-message-highlight")}>
        {showHeader && (
          <ChatRowHeader
            authorName={props.authorName}
            timestamp={props.timestamp}
            edited={props.edited}
            labels={props.labels}
            actions={actions}
          />
        )}
        {props.quote}
        <ChatRowContent {...props} showHeader={showHeader} />
      </div>
    </div>
  );
}
